import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import { task, timeout } from 'ember-concurrency';
import type { OtaId } from 'frontend-utils/utils/constants/constant-values';
import { pushAndReturnPayload } from 'frontend-utils/utils/ember-data/push-and-return-payload';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { Liveupdate } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { LIVEUPDATE_TYPE_RATES } from 'lumen/services/liveupdate';
import {
  APIGATEWAY,
  RATE_PARAMS,
} from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import { mapRateParamToAPIParams } from 'lumen/utils/rates-request';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface LiveupdateBulkConfig {
  from_date: string;
  hotel: number;
  labels: string;
  ota: string;
  params: {
    los: number;
    mealtype: number | null;
    membershiptype: number;
    persons: number;
    roomtype: string | null;
  };
  skip_compset_shopping: boolean;
  to_date: string;
  type: string;
}

export type LiveupdateBulkOptions = {
  los?: number;
  mealtype?: number;
  persons?: number;
};

interface LiveupdateBulkResponse extends Record<string, object[] | number> {
  id: number;
  liveupdates: [Liveupdate];
}

/** Parameters passed to observer functions when liveupdate completes */
type ObservedFunctionParams = { fromDate: string; toDate: string };

/** Observer callback function signature for liveupdate completion notifications */
type ObserverFunction = (params: ObservedFunctionParams) => void;

const LIVEUPDATE_END_STATE_STATUSES_MAPPING = {
  101: 'STATUS_LIVE_FINISHED_COMPLETE',
  102: 'STATUS_LIVE_FINISHED_COMPLETE_UNCHECKED',
  103: 'STATUS_LIVE_FINISHED_COMPLETE_SENT',
  104: 'STATUS_LIVE_FINISHED_COMPLETE_FULL_FEEDBACK',
  105: 'STATUS_LIVE_FINISHED_INCOMPLETE',
  106: 'STATUS_LIVE_FINISHED_INCOMPLETE_SENDABLE',
  107: 'STATUS_LIVE_FINISHED_INCOMPLETE_SENT',
  110: 'STATUS_LIVE_SOFT_ERROR',
  115: 'STATUS_LIVE_HARD_ERROR',
} as const;

const LIVEUPDATE_END_STATE_STATUSES = Object.keys(
  LIVEUPDATE_END_STATE_STATUSES_MAPPING,
).map(Number);

const LIVEUPDATE_CHANNEL_PRICING_LABEL = 'smart_distribution';

const POLLING_INTERVAL_IN_MILLISECONDS = 10000;

/**
 * Service that defines the business logic of the Smart Distribution liveupdate
 *
 * CLOSING THE LOOP TODO: The poller part is mainly based upon the central-mapping/saving.
 * We needed a quick/lightweight poller to be able to validate our actual feature flow.
 */
export default class ChannelPricingLiveupdateService extends Service {
  @service declare private ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  // CLOSING THE LOOP TODO: Is this actually useful? Or can we rely on the task lifecycle info alone?
  @tracked isProcessing = false;

  activeLiveupdateDateRange: { fromDate: Dayjs; toDate: Dayjs } | null = null;

  fromDate: string | null = null;

  /** List of observer functions to notify when liveupdate completes */
  observers: ((params: ObservedFunctionParams) => void)[] = [];

  toDate: string | null = null;

  private createBulkLiveupdateTask = task(
    async (otaIds: OtaId[], liveupdateOptions: LiveupdateBulkOptions) => {
      if (
        !this.hotelAndMarketService.hotel.id ||
        !otaIds ||
        this.fromDate === null ||
        this.toDate === null
      ) {
        console.error(
          '[Liveupdate] ✗ Cannot start liveupdate: Missing required data',
          {
            hotelId: this.hotelAndMarketService.hotel.id,
            hasOtas: Boolean(otaIds),
            fromDate: this.fromDate,
            toDate: this.toDate,
          },
        );
        return null;
      }

      if (this.areTasksRunning) {
        return null;
      }

      const liveupdateBulkConfigs: LiveupdateBulkConfig[] = [];

      otaIds.forEach((otaId) => {
        const liveupdateBulkConfig: LiveupdateBulkConfig = {
          skip_compset_shopping: true,
          hotel: parseInt(this.hotelAndMarketService.hotel.id!, 10),
          type: LIVEUPDATE_TYPE_RATES,
          ota: otaId,
          from_date: this.fromDate as string,
          to_date: this.toDate as string,
          params: {
            los: liveupdateOptions.los ?? 1,
            mealtype: liveupdateOptions.mealtype ?? null,
            membershiptype: 0,
            persons: liveupdateOptions.persons ?? 0,
            roomtype: null,
          },
          labels: LIVEUPDATE_CHANNEL_PRICING_LABEL,
        };

        mapRateParamToAPIParams(
          RATE_PARAMS.LOWEST,
          liveupdateBulkConfig.params,
        );
        liveupdateBulkConfigs.push(liveupdateBulkConfig);
      });

      let results = null;

      try {
        results = await this.launchBulkLiveupdateRequestTask.perform(
          this.hotelAndMarketService.hotel.id,
          liveupdateBulkConfigs,
        );
      } catch (error) {
        console.error(
          '[Liveupdate] ✗ Failed to launch bulk liveupdate request',
          error,
        );
      }

      if (!results) {
        return null;
      }

      return results;
    },
  );

  /**
   * Initiates a bulk liveupdate request to the API
   * @param subscription_id - The hotel subscription ID
   * @param bulkObjects - Array of liveupdate configurations to process
   * @returns Object containing liveupdate records
   */
  private launchBulkLiveupdateRequestTask = task(
    { restartable: true },
    async (subscription_id: string, bulkObjects: LiveupdateBulkConfig[]) => {
      const params: Record<string, string | number> = { subscription_id };

      try {
        const requestUrl = formatUrl(
          `/${APIGATEWAY}/liveshop/bulk`,
          params,
          this.ajax.host,
        );

        const response = await this.ajax.post<LiveupdateBulkResponse>(
          requestUrl,
          {
            data: { liveupdates: bulkObjects },
            contentType: 'application/json',
          },
        );

        const { id, liveupdates } = response;

        const fromStore = pushAndReturnPayload(this.store, { liveupdates });

        return { id, ...fromStore } as LiveupdateBulkResponse;
      } catch (error) {
        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  /**
   * Schedules the next status polling check after the configured interval
   * @param subscription_id - The hotel subscription ID
   * @param bulk_push_id - The bulk liveupdate ID to check status for
   */
  private pollerScheduleStatusTask = task(
    { restartable: true },
    async (subscription_id: string, bulk_push_id: number): Promise<void> => {
      console.log('[Liveupdate] Scheduling next status check in');
      await timeout(POLLING_INTERVAL_IN_MILLISECONDS);

      void this.pollerStatusTask.perform(subscription_id, bulk_push_id);
    },
  );

  /**
   * Polls the status of liveupdate processing and continues until all liveupdates reach an end state.
   * Notifies observers when processing completes successfully.
   * @param subscription_id - The hotel subscription ID
   * @param bulk_push_id - The bulk liveupdate ID to check status for
   */
  private pollerStatusTask = task(
    { restartable: true },
    async (subscription_id: string, bulk_push_id: number) => {
      this.isProcessing = true;

      try {
        console.log(
          `[Liveupdate] Polling status | Bulk ID: ${bulk_push_id} | Subscription ID: ${subscription_id}`,
        );
        const statusResponse = await this.store.query<Liveupdate>(
          'liveupdate',
          {
            namespace: 'api/v3',
            typeKey: 'liveupdates/app/bulk/status',
            subscription_id: this.hotelAndMarketService.hotel.id,
            bulk_liveupdate_id: bulk_push_id,
          },
        );

        const liveupdatesToCheck = [...statusResponse];

        if (liveupdatesToCheck[0]) {
          this.activeLiveupdateDateRange = {
            fromDate: liveupdatesToCheck[0].fromDate,
            toDate: liveupdatesToCheck[0].toDate,
          };
        }

        const shouldContinuePolling = liveupdatesToCheck.some((liveupdate) => {
          return !LIVEUPDATE_END_STATE_STATUSES.includes(liveupdate.status);
        });

        console.log(`[Liveupdate] Continue polling: ${shouldContinuePolling}`);
        console.table(
          liveupdatesToCheck.map((liveupdate) => ({
            OTA: liveupdate.ota.name,
            'Status Code': liveupdate.status,
            'Status Meaning':
              LIVEUPDATE_END_STATE_STATUSES_MAPPING[
                liveupdate.status as keyof typeof LIVEUPDATE_END_STATE_STATUSES_MAPPING
              ] ?? 'In progress',
          })),
        );

        // If processing is complete, don't schedule another poll and check to kickoff the the post liveupdate behavior
        if (!shouldContinuePolling) {
          this.isProcessing = false;

          this.activeLiveupdateDateRange = null;

          console.log(
            '[Liveupdate] ✓ Processing completed successfully | Bulk ID:',
            bulk_push_id,
          );

          // Callback for side-effects, like refetching data
          this.notify({
            fromDate: this.fromDate!,
            toDate: this.toDate!,
          });

          return;
        }

        void this.pollerScheduleStatusTask.perform(
          subscription_id,
          bulk_push_id,
        );
      } catch (error) {
        this.errorTracking.captureException(error);

        void this.pollerScheduleStatusTask.perform(
          subscription_id,
          bulk_push_id,
        );
      }
    },
  );

  /**
   * Starts a bulk liveupdate for the specified OTAs and begins status polling.
   * Requires fromDate, toDate, and hotel ID to be set before calling.
   * @param otaIds - Array of OTA ids to fetch rates for
   */
  startLiveupdateTask = task(
    async (
      otaIds: OtaId[],
      liveupdateOptions: LiveupdateBulkOptions,
    ): Promise<LiveupdateBulkResponse | null> => {
      const results = await this.createBulkLiveupdateTask.perform(
        otaIds,
        liveupdateOptions,
      );

      if (results) {
        this.startPolling(this.hotelAndMarketService.hotel.id!, results.id);
      }

      return results;
    },
  );

  get areTasksRunning(): boolean {
    return (
      this.launchBulkLiveupdateRequestTask.isRunning ||
      this.pollerScheduleStatusTask.isRunning ||
      this.pollerStatusTask.isRunning
    );
  }

  /**
   * Fetches active liveupdates for the current hotel.
   * Filters results to only include liveupdates labeled with LIVEUPDATE_CHANNEL_PRICING_LABEL that fit within the synced date range.
   * @returns Array of active Channel Pricing liveupdate records, or empty array on error
   */
  async fetchActiveChannelPricingLiveupdates(): Promise<Liveupdate[]> {
    if (!this.hotelAndMarketService.hotel?.id) {
      console.error(
        '[Liveupdate] ✗ Cannot fetch active liveupdates: Hotel ID not available',
      );
      return [];
    }

    try {
      const allLiveupdates = await this.store.query<Liveupdate>('liveupdate', {
        hotel_id: this.hotelAndMarketService.hotel.id,
      });

      // Convert service date strings to Dayjs for comparison
      const viewFromDate = this.hotelAndMarketService.toLocalTime(
        this.fromDate,
      );
      const viewToDate = this.hotelAndMarketService.toLocalTime(this.toDate);

      const filteredLiveupdates = allLiveupdates.filter((liveupdate) => {
        if (
          !liveupdate.bulkLiveupdateId ||
          liveupdate.labels !== LIVEUPDATE_CHANNEL_PRICING_LABEL ||
          !liveupdate.fromDate ||
          !liveupdate.toDate
        ) {
          return false;
        }

        // Check if liveupdate date range overlaps with the viewed date range
        // This handles edge cases where users with different locale week settings
        // (Monday-Sunday vs Sunday-Saturday)
        const hasOverlap =
          liveupdate.fromDate.isSameOrBefore(viewToDate, 'day') &&
          liveupdate.toDate.isSameOrAfter(viewFromDate, 'day');

        return hasOverlap;
      });

      return [...filteredLiveupdates];
    } catch (error) {
      this.errorTracking.captureException(error);
      console.error(
        '[Liveupdate] ✗ Failed to fetch active liveupdates:',
        error,
      );
      return [];
    }
  }

  /**
   * Checks if a given date falls within the active liveupdate date range
   * @param date - Date to check (Dayjs object)
   * @returns True if the date is within the active liveupdate range, false otherwise
   */
  isDateWithinActiveLiveupdateRange(date: Dayjs): boolean {
    if (!this.activeLiveupdateDateRange) {
      return false;
    }

    const { fromDate, toDate } = this.activeLiveupdateDateRange;

    return date.isBetween(fromDate, toDate, 'day', '[]');
  }

  /**
   * Notifies all subscribed observers with the synced date range that's currently stored in the service.
   * @param params - Date range parameters to pass to observers
   */
  notify({ fromDate, toDate }: ObservedFunctionParams): void {
    this.observers.forEach((observer) => observer({ fromDate, toDate }));
  }

  /**
   * Initiates status polling for a bulk liveupdate process
   * @param subscriptionId - The hotel subscription ID
   * @param bulkPushId - The bulk liveupdate ID to poll status for
   */
  startPolling(subscriptionId: string, bulkPushId: number): void {
    if (!this.areTasksRunning) {
      console.log('[Liveupdate] Starting liveupdate polling...');
      void this.pollerStatusTask.perform(subscriptionId, bulkPushId);
    }
  }

  /**
   * Stops the polling process and cancels all running tasks
   */
  async stopPolling(): Promise<void> {
    if (this.areTasksRunning) {
      console.log('[Liveupdate] ⚠ Stopping polling and cancelling tasks...');

      await this.launchBulkLiveupdateRequestTask.cancelAll();
      await this.pollerScheduleStatusTask.cancelAll();
      await this.pollerStatusTask.cancelAll();

      this.isProcessing = false;
      this.activeLiveupdateDateRange = null;
    }
  }

  /**
   * Subscribes a callback function to be notified when liveupdate completes
   * @param func - Observer function to call on completion
   */
  subscribe(func: ObserverFunction): void {
    this.observers.push(func);
  }

  /**
   * Unsubscribes a callback function from liveupdate completion notifications
   * @param func - Observer function to remove
   */
  unsubscribe(func: ObserverFunction): void {
    this.observers = this.observers.filter((observer) => observer !== func);
  }
}

declare module '@ember/service' {
  interface Registry {
    'channel-pricing/liveupdate': ChannelPricingLiveupdateService;
  }
}
